import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';

import { appsignal } from '../appsignal';
import AppsignalErrorBoundary from './AppsignalErrorBoundary';

type ComponentType<R, P> =
  | React.ComponentClass<P>
  | React.FunctionComponent<P>
  | React.ForwardRefExoticComponent<React.PropsWithoutRef<P> & React.RefAttributes<R>>;

export default class Renderer<RefType, PropsType> {
  private component: ComponentType<RefType, PropsType>;
  private props: any;
  private root: Root | undefined;
  private renderVersion = 0;

  ref: RefType | null = null;
  rootElement: Element | undefined;

  constructor(
    component: ComponentType<RefType, PropsType>,
    {
      renderOnInit = true,
      props,
      as = 'div',
      rootElement,
    }: { renderOnInit?: boolean; props: PropsType; as?: string; rootElement?: Element },
  ) {
    this.component = component;
    this.props = props;

    if (rootElement) {
      this.rootElement = rootElement;
    } else {
      this.rootElement = document.createElement(as);
      document.body.appendChild(this.rootElement);
    }

    this.root = createRoot(this.rootElement);

    if (renderOnInit) this.render();
  }

  public render() {
    if (this.rootElement) {
      const Component = this.component as React.ElementType;
      const componentName =
        (this.component as { displayName?: string; name?: string }).displayName ||
        (this.component as { displayName?: string; name?: string }).name ||
        'AnonymousRendererComponent';
      const content = (
        <Component
          {...this.props}
          rootElement={this.rootElement}
          ref={(ref: RefType | null) => {
            this.ref = ref;
          }}
        />
      );

      this.root?.render(
        appsignal ? (
          <AppsignalErrorBoundary
            componentName={componentName}
            resetKey={this.renderVersion}
            rootElement={this.rootElement}
            source='react-renderer'
          >
            {content}
          </AppsignalErrorBoundary>
        ) : (
          content
        ),
      );
    }
  }

  public updateProps(props: Partial<PropsType>): void {
    this.props = {
      ...this.props,
      ...props,
    };
    this.renderVersion += 1;

    this.render();
  }

  public destroy() {
    const root = this.root;
    const rootElement = this.rootElement;

    this.root = undefined;
    this.rootElement = undefined;

    const cleanup = () => {
      root?.unmount();
      rootElement?.remove();
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(cleanup);
    } else {
      setTimeout(cleanup, 0);
    }
  }
}
